import React, { useEffect, useState, useRef, useCallback } from 'react'

import { Router, useRouter } from 'next/router';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';

// import Plus from 'components/Shared/icons/plus';
import Cancel from 'components/Shared/icons/cancel';
import SearchIcon from 'components/Shared/icons/search';
import LoadingSpinner from 'components/Shared/icons/LoadingSpinner';
import DGAInput from 'components/Shared/DGA-inputs/DGA-input';
import Close from 'components/Shared/icons/close';
import { DGAButton } from 'components/Shared/dga-button/DGAButton';
import Plus from 'components/Shared/icons/plus';
import axios from 'axios';

interface HeaderSearchPanelProps {
    SearchButtonText: string;
    SearchInputPlaceholder: string;
    SuggestionsHeader: string;
    SuggestionItems: any[];
    isSearchPanelOpen: boolean;
    setIsSearchPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    locale?: string;
}

const HeaderSearchPanel: React.FC<HeaderSearchPanelProps> = ({
    SearchButtonText,
    SearchInputPlaceholder,
    SuggestionsHeader,
    SuggestionItems,
    isSearchPanelOpen,
    setIsSearchPanelOpen,
    locale
}) => {

    const router = useRouter();

    const [search, setSearch] = useState<string>("");
    const [apiSuggestions, setApiSuggestions] = useState<any[]>([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [isRouteLoading, setIsRouteLoading] = useState(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Fetch search suggestions from API
    const fetchSearchSuggestions = useCallback(async (searchTerm: string) => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setApiSuggestions([]);
            return;
        }

        setIsLoadingSuggestions(true);
        try {
            const response = await axios.post(
                `${process.env.NEXT_PUBLIC_SITECORE_REST}/api/Search/GetAll`,
                {
                    culture: locale || 'en',
                    PageNumber: 0,
                    PageSize: 15, // Limit to 15 suggestions
                    order: '',
                    search: searchTerm,
                }
            );

            if (response.data?.AwardsMediaCenter) {
                setApiSuggestions(response.data.AwardsMediaCenter);
            } else {
                setApiSuggestions([]);
            }
        } catch (error) {
            console.error('Error fetching search suggestions:', error);
            setApiSuggestions([]);
        } finally {
            setIsLoadingSuggestions(false);
        }
    }, [locale]);

    useEffect(() => {
        const handleStart = () => setIsRouteLoading(true);
        const handleEnd = () => setIsRouteLoading(false);

        Router.events.on("routeChangeStart", handleStart);
        Router.events.on("routeChangeComplete", handleEnd);
        Router.events.on("routeChangeError", handleEnd);

        return () => {
            Router.events.off("routeChangeStart", handleStart);
            Router.events.off("routeChangeComplete", handleEnd);
            Router.events.off("routeChangeError", handleEnd);

            setIsSearchPanelOpen(false);
        };
    }, []);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Reset suggestions when panel closes
    useEffect(() => {
        if (!isSearchPanelOpen) {
            setSearch("");
            setApiSuggestions([]);
        }
    }, [isSearchPanelOpen]);

    if (!isSearchPanelOpen) return <></>;

    // Debounced search handler
    const handleSearchChange = (value: string) => {
        setSearch(value);

        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer for debounced API call
        debounceTimerRef.current = setTimeout(() => {
            fetchSearchSuggestions(value);
        }, 300); // 300ms debounce
    };

    const handleSearchFormSubmit = async (e: any) => {
        e.preventDefault();

        try {
            const formData = new FormData(e.currentTarget);

            const searchInput = formData.get("searchInput") as string;

            if (searchInput) {
                await router.push({
                    pathname: "/modules/pages/search-results",
                    query: { search: searchInput },
                });
                setIsSearchPanelOpen(false);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleSuggestionClick = async (suggestionValue: string) => {
        setSearch(suggestionValue);
        setApiSuggestions([]);

        try {
            if (suggestionValue) {
                await router.push({
                    pathname: "/modules/pages/search-results",
                    query: { search: suggestionValue },
                });
                setIsSearchPanelOpen(false);
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div className="absolute top-full left-0 right-0 w-full bg-white py-8 rounded-b-3xl z-[99] animate-slide-in shadow-2xl">
            <div className="responsive flex flex-col items-start gap-6 lg:gap-10">
                {/* close search panel button */}
                <button
                    onClick={() => setIsSearchPanelOpen(false)}
                    className="flex items-center justify-center w-fit self-end p-[6px] hover:!bg-[#F6F4F3] transition-colors duration-300 rounded"
                >
                    <Cancel color="#161616" className="w-6 h-6" />
                </button>

                {/* search form */}
                <form
                    onSubmit={handleSearchFormSubmit}
                    className="flex items-center gap-4 w-full"
                >
                    {/* search input with dropdown container */}
                    <div className="relative flex-1">
                        {/* search input field, with search icon and mic icon */}
                        <DGAInput
                            type="text"
                            name="searchInput"
                            placeholder={SearchInputPlaceholder}
                            InputSize='lg'
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            startIcon={
                                <button
                                    type="submit"
                                    disabled={isRouteLoading}
                                    aria-label={SearchButtonText || "Search"}
                                    className="disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <SearchIcon
                                        color={"#161616"}
                                        classes="w-5 h-5 cursor-pointer"
                                        aria-hidden
                                    />
                                </button>
                            }
                            endIcon={
                                <button
                                    type="button"
                                    aria-label="Clear search"
                                    className={`transition-all ${search ? "block opacity-100" : "opacity-0 hidden"}`}
                                    onClick={() => {
                                        setSearch('');
                                        setApiSuggestions([]);
                                    }}
                                >
                                    <Close aria-hidden />
                                </button>
                            }
                        />

                        {/* API suggestions dropdown */}
                        {search && (isLoadingSuggestions || apiSuggestions.length > 0) && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg z-50 max-h-[350px] overflow-y-auto">
                                {isLoadingSuggestions && (
                                    <div className="flex items-center justify-center gap-2 px-4 py-3 text-neutral-500">
                                        <LoadingSpinner className="!w-4 !h-4" />
                                        {/* <span className="text-sm">Loading suggestions...</span> */}
                                    </div>
                                )}
                                {!isLoadingSuggestions && apiSuggestions.length > 0 && (
                                    <div className="py-2">
                                        {apiSuggestions.map((item: any, index: number) => {
                                            // Extract the last part after "/" from itemPath
                                            // const getPageName = (itemPath: string) => {
                                            //     if (!itemPath) return '';
                                            //     const parts = itemPath.split('/').filter(part => part);
                                            //     return parts.length > 0 ? parts[parts.length - 1] : '';
                                            // };

                                            // const pageName = getPageName(item?.itemPath || '');
                                            // const displayTitle = pageName ? `${item?.title || ''} - ${pageName}` : item?.title || '';
                                            const displayTitle = item?.title || '';
                                            return (
                                                <button
                                                    key={item?.itemId || index}
                                                    onClick={() => handleSuggestionClick(item?.title || '')}
                                                    className="w-full text-start px-4 py-2 hover:bg-neutral-100 transition-colors flex flex-col gap-1"
                                                >
                                                    <span className="text-primary-1000 font-medium text-sm">{displayTitle}</span>
                                                    {item?.description && (
                                                        <RichText
                                                            field={{ value: item.description }}
                                                            className="text-neutral-600 text-xs line-clamp-2"
                                                        />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* search submit button with loading spinner when searching is in progress */}
                    {SearchButtonText && (
                        <DGAButton
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="self-end !ring-0"
                            buttonContent={
                                isRouteLoading ? (<LoadingSpinner />) : SearchButtonText
                            }
                            disabled={isRouteLoading}
                        />
                    )}
                </form>

                {/* Static suggestions section - always visible */}
                <div className="flex flex-col items-start gap-4 w-full">
                    {SuggestionsHeader && (
                        <h1 className="text-primary-600 font-semibold text-lg">
                            {SuggestionsHeader}
                        </h1>
                    )}

                    {/* Static suggestions tags */}
                    <div className="flex items-center flex-wrap gap-2">
                        {SuggestionItems.map((item: any) => (
                            <DGAButton
                                key={item.id}
                                variant="secondarySolid"
                                size="md"
                                className="flex items-center gap-1"
                                onClick={() => handleSuggestionClick(item?.fields?.Message?.value)}
                                buttonContent={
                                    <>
                                        <Plus aria-label="" aria-hidden={true} />
                                        {item?.fields?.Message?.value}
                                    </>
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default React.memo(HeaderSearchPanel);